import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { type PaginationOptions } from '../../../api/pagination'
import { fetchChannels, fetchChannel, fetchMessages, fetchUnreadMessagesCountApi } from '../api/chatApi'
import { type Channel, type Message } from '../utils/types'
import { type PaginatedResponse } from '../../../api/client'
import { chatChannelKeys, chatMessageKeys } from '../utils/queryKeys'

/**
 * Hook to fetch paginated channels
 */
export function useChannels(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Channel>, Error> {
  return useQuery({
    queryKey: chatChannelKeys.list(options),
    queryFn: () => fetchChannels(options),
    enabled,
  })
}

/**
 * Hook to fetch a single channel
 */
export function useChannel(
  channelId: string | number,
  expand?: string[],
  enabled = true
): UseQueryResult<Channel, Error> {
  return useQuery({
    queryKey: chatChannelKeys.detail(typeof channelId === 'number' ? channelId : Number(channelId)),
    queryFn: () => fetchChannel(channelId, expand),
    enabled: enabled && Boolean(channelId),
  })
}

/**
 * Hook to fetch messages for a channel
 */
export function useMessages(
  channelId: string | number,
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Message>, Error> {
  return useQuery({
    queryKey: chatMessageKeys.list(options, undefined),
    queryFn: () => fetchMessages(channelId, options),
    enabled: enabled && Boolean(channelId),
  })
}

/**
 * Hook to fetch total unread messages count
 */
export function useUnreadMessagesCount(
  enabled = true
): UseQueryResult<number, Error> {
  return useQuery({
    queryKey: ['chat', 'unread-messages-count'],
    queryFn: fetchUnreadMessagesCountApi,
    enabled,
  })
}

